/**
 * BrowserMediaPort — default implementation of MediaPort for production use.
 *
 * Delegates each method to the real browser API. Injected at the widget root
 * in sub-task 1b; never imported directly by route handlers or React components.
 *
 * Clipboard fallback mirrors the legacy pattern in apps/mirrably-demo/index.html:1218-1226:
 * creates a temporary textarea, selects its content, and calls document.execCommand('copy').
 */

import type { MediaPort, ClipboardPort } from "./media-port.js";

class BrowserClipboardPort implements ClipboardPort {
  async writeText(text: string): Promise<void> {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      return navigator.clipboard.writeText(text);
    }
    // Legacy fallback — mirrors apps/mirrably-demo/index.html:1218-1226
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
    } finally {
      document.body.removeChild(textarea);
    }
  }
}

export class BrowserMediaPort implements MediaPort {
  clipboard: ClipboardPort = new BrowserClipboardPort();

  async getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream> {
    return navigator.mediaDevices.getUserMedia(constraints);
  }

  async fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    return globalThis.fetch(input, init);
  }
}
